import React, { useState } from 'react';
import { 
  BarChart3, 
  Target, 
  Users, 
  Gift, 
  LogOut, 
  Leaf,
  Menu,
  X,
  TrendingUp,
  CheckSquare,
  Activity
} from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';
import AdminImpact from './AdminImpact';
import AdminMissions from './AdminMissions';
import AdminUsers from './AdminUsers';
import AdminRewards from './AdminRewards';
import AdminLevels from './AdminLevels';
import AdminCompletions from './AdminCompletions';
import AdminMissionImpacts from './AdminMissionImpacts';

const tabs = [
  { id: 'impact', label: 'Impact Stats', icon: BarChart3 },
  { id: 'missions', label: 'Missions', icon: Target },
  { id: 'mission-impacts', label: 'Mission Impacts', icon: Activity },
  { id: 'completions', label: 'Completions', icon: CheckSquare },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'rewards', label: 'Rewards', icon: Gift },
  { id: 'levels', label: 'Levels', icon: TrendingUp }
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('impact');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { adminUser, signOut } = useAdmin();

  const renderContent = () => {
    switch (activeTab) {
      case 'impact':
        return <AdminImpact />;
      case 'missions':
        return <AdminMissions />;
      case 'mission-impacts':
        return <AdminMissionImpacts />;
      case 'completions':
        return <AdminCompletions />;
      case 'users':
        return <AdminUsers />;
      case 'rewards':
        return <AdminRewards />;
      case 'levels':
        return <AdminLevels />;
      default:
        return <AdminImpact />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-2">
                <div className="bg-emerald-500 p-2 rounded-lg">
                  <Leaf className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Yashnar Admin</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">Platform Management</p>
                </div>
              </div>
            </div>

            {/* User Info and Actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">Welcome, {adminUser?.username}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              
              <button
                onClick={signOut}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2"
                title="Sign Out"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Mobile Navigation Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <nav className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="flex items-center justify-between p-4 lg:hidden">
            <span className="text-lg font-semibold text-gray-900">Navigation</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="px-4 py-6 space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 lg:ml-0 pt-8 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}